import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";

const FAQ = [
  { q: "Is this legal advice?", a: "No. NDIS 0138 Readiness provides operational readiness mapping, evidence gap identification, documentation risk signals and referral triggers. Legal interpretation, document drafting, disputes, privacy obligations and registration decisions should be referred to appropriate professionals." },
  { q: "Does NDIS 0138 Readiness certify compliance?", a: "No. It does not certify provider compliance, audit readiness, registration success or legal status. It helps identify what evidence appears present, missing, unclear or requiring professional review." },
  { q: "Can NDIS 0138 Readiness guarantee NDIS registration?", a: "No. Registration and audit outcomes sit with the relevant regulator, auditor or professional adviser. NDIS 0138 Readiness only supports preparation and readiness mapping." },
  { q: "Are you an Approved Quality Auditor?", a: "No. NDIS 0138 Readiness is not an Approved Quality Auditor report, official NDIS Commission tool, audit report or registration decision-maker." },
  { q: "What happens if legal issues are found?", a: "The system flags a referral trigger. The provider can then seek independent legal, privacy, audit, HR, accounting or regulatory advice depending on the issue." },
  { q: "Do you review service agreements?", a: "NDIS 0138 Readiness can identify whether a service agreement template exists and whether it may require review. It does not provide legal drafting or legal approval of agreements." },
  { q: "Do you collect participant-identifying information?", a: "The intake is designed around provider systems and evidence maturity. It asks users not to enter participant names, addresses, medical histories, identifying incident details or unnecessary worker private information." },
  { q: "What should be referred to a lawyer?", a: "Service agreements, termination rights, privacy obligations, disputes, contractor arrangements, tenancy-support overlap, business structure risk and registration-law questions may require independent legal advice." },
  { q: "What is the difference between readiness mapping and legal compliance?", a: "Readiness mapping identifies operational evidence, gaps and next actions. Legal compliance is a legal conclusion that NDIS 0138 Readiness does not make." }
];

export default function WhatWeDoPage() {
  return (
    <PageShell>
      <section className="container py-10 lg:py-14">
        <Button asChild variant="ghost" className="mb-8 rounded-sm px-0 text-sm text-muted-foreground hover:bg-transparent hover:text-foreground">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Back to overview
          </Link>
        </Button>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Scope & boundaries</p>
            <h1 className="mt-3 font-display text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
              What We Do / What We Do Not Do
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
              Spiral Beyond Compliance uses NDIS 0138 Readiness to help NDIS providers map operational readiness, evidence gaps, documentation risk and referral triggers before legal, audit or registration review.
            </p>
            <div className="mt-6 rounded-sm border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Boundary statement: </strong>
              NDIS 0138 Readiness is an operational readiness and evidence-mapping model. It is not legal advice, audit certification, NDIS registration approval, or a guarantee of compliance outcomes.
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="paper-card space-y-4 p-6">
              <p className="eyebrow">Use case</p>
              <h2 className="font-display text-2xl text-foreground">Readiness, evidence and referral triggers.</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                The tool supports preparation. It does not replace legal advice, an auditor, the NDIS Commission, clinical governance, emergency response or professional judgement.
              </p>
              <Button asChild className="rounded-sm">
                <Link to="/intake">
                  Begin readiness intake
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <section className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="paper-card p-6">
            <p className="eyebrow">What we do</p>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>• Map operational readiness indicators.</li>
              <li>• Identify evidence gaps and documentation risk.</li>
              <li>• Flag legal, privacy, audit, safeguarding or registration referral triggers.</li>
              <li>• Support a practical 30 / 60 / 90 day readiness plan.</li>
            </ul>
          </div>
          <div className="paper-card p-6">
            <p className="eyebrow">What we do not do</p>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>• We do not provide legal advice.</li>
              <li>• We do not provide audit certification.</li>
              <li>• We do not provide NDIS registration approval.</li>
              <li>• We do not guarantee legal, audit, registration or regulator outcomes.</li>
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <p className="eyebrow">FAQ</p>
          <div className="mt-5 grid grid-cols-1 gap-4">
            {FAQ.map((item) => (
              <article key={item.q} className="rounded-sm border border-border bg-card p-5">
                <h3 className="font-display text-lg text-foreground">{item.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </article>
            ))}
          </div>
        </section>
      </section>
    </PageShell>
  );
}
